import { useCallback, useEffect, useRef, useState } from 'react';
import { findScrollableParent } from './utils';

/**
 * Custom hook to observe an element using IntersectionObserver
 */
const useIntersectionObserver = (
  callback: () => void,
  root: HTMLElement | null | undefined,
  rootMargin: string,
  threshold: number
) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const elementRef = useRef<HTMLDivElement | null>(null);
  const callbackRef = useRef(callback);
  const [isElementMounted, setIsElementMounted] = useState(false);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const setElementRef = useCallback((node: HTMLDivElement | null) => {
    elementRef.current = node;
    setIsElementMounted(node !== null);
  }, []);

  useEffect(() => {
    if (!isElementMounted || !elementRef.current) return;

    let resolvedRoot: HTMLElement | null = null;
    if (root !== undefined) {
      resolvedRoot = root;
    } else {
      resolvedRoot = findScrollableParent(elementRef.current);
    }

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            callbackRef.current();
          }
        });
      },
      { root: resolvedRoot, rootMargin, threshold }
    );

    observerRef.current.observe(elementRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [root, rootMargin, threshold, isElementMounted]);

  return { setElementRef };
};

export { useIntersectionObserver };
